
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { MessageList } from "@/components/Chat/MessageList";
import { ChatInput } from "@/components/Chat/ChatInput";
import { ChannelHeader } from "@/components/Chat/ChannelHeader";
import { useChannelChat } from "@/hooks/chat";

const ChannelChat = () => {
  const { groupId, channelId } = useParams<{ groupId: string; channelId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const {
    channel,
    group,
    messages,
    currentMember,
    loading,
    expiryTime
  } = useChannelChat(channelId, groupId, user?.id);

  if (loading) {
    return (
      <Layout>
        <div className="container max-w-7xl mx-auto px-4 py-8 h-screen">
          <div className="text-center">Loading channel...</div>
        </div>
      </Layout>
    );
  }

  if (!channel || !group) {
    return (
      <Layout>
        <div className="container max-w-7xl mx-auto px-4 py-8 h-screen">
          <div className="text-center">Channel not found</div>
          <div className="mt-4 text-center">
            <Button onClick={() => navigate(`/groups/${groupId}`)}>
              Return to Group
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Handle user banned case
  if (currentMember?.member?.isBanned) {
    return (
      <Layout>
        <div className="container max-w-7xl mx-auto px-4 py-8 h-screen">
          <Card>
            <CardContent className="p-6 text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="mb-4">You have been banned from this group.</p>
              <Button onClick={() => navigate("/groups")}>
                Return to Groups
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-7xl mx-auto px-4 py-4 md:py-8 flex flex-col h-[calc(100vh-140px)]">
        {/* Channel Header */}
        <ChannelHeader 
          group={group}
          channel={channel}
          groupId={groupId || ""}
          isMobile={isMobile}
          expiryTime={expiryTime}
          onBackClick={() => navigate(`/groups/${groupId}`)}
        />
        
        {/* Messages Container */}
        <MessageList 
          messages={messages || []}
          channel={channel}
        />
        
        {/* Message Input */}
        <div className="mt-4 space-y-2">
          {user && (
            <ChatInput 
              channelId={channelId || ""}
              userId={user.id}
              currentMember={currentMember?.member}
              channel={channel}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ChannelChat;
